import type { RuntimeTree } from './instances';
import type { ChosenOption, NodeVisibility, VisibilityResult } from './visibility';
import {
    CONDITION_RESULT_FROM_CHILDREN,
    CONDITION_RESULT_NO_BUBBLE,
    CONDITION_RESULT_SCALE,
    CONDITION_RESULT_SEVERITY_ORDER,
    type ConditionResult,
    type ConditionResultCode,
    type NodeId,
} from './types';

/**
 * ResultResolver — het oordeel per knoop.
 *
 * `result` is een enum, geen getal: de ernstvolgorde is
 * `-5 > -4 > -3 > 1 > 2 > 4 > 6 > 8 > 0 > -1 > -2` en de eerste die voorkomt wint.
 * `-1` ("uit subdiagrammen") delegeert het oordeel aan de kinderen.
 * Geverifieerd op 455/455 knopen (`graph-semantics.md` §4).
 */

const SEVERITY_RANK = new Map<ConditionResultCode, number>(
    CONDITION_RESULT_SEVERITY_ORDER.map((code, index) => [code, index]),
);

/** De hoogst geplaatste waarde uit de lijst, of `null` als de lijst leeg is. */
export function severityPick(codes: readonly ConditionResultCode[]): ConditionResult {
    let best: ConditionResult = null;
    let bestRank = Number.POSITIVE_INFINITY;

    for (const code of codes) {
        const rank = SEVERITY_RANK.get(code);

        if (rank !== undefined && rank < bestRank) {
            bestRank = rank;
            best = code;
        }
    }

    return best;
}

/**
 * Rondt het gemiddelde van de positieve kindscores af op de schaal 1/2/4/6/8.
 * Halve waarden gaan naar beneden.
 *
 * De vergelijking gebeurt met gehele getallen (`|s · n − som|`) zodat client en
 * server tot op het laatste cijfer hetzelfde afronden.
 */
export function roundToScale(sum: number, count: number): ConditionResultCode {
    let best = CONDITION_RESULT_SCALE[0] as ConditionResultCode;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const step of CONDITION_RESULT_SCALE) {
        const distance = Math.abs(step * count - sum);

        if (distance < bestDistance) {
            bestDistance = distance;
            best = step;
        }
    }

    return best;
}

/**
 * Het oordeel van één knoop, gegeven de al berekende oordelen van zijn kinderen.
 *
 * Bevat de knoop zelf een `-1` of levert hij helemaal geen resultaat, dan telt
 * het afgeronde gemiddelde van de positieve kindscores. Anders telt uitsluitend
 * het eigen oordeel en worden de kinderen genegeerd.
 */
export function resolveNodeResult(
    chosen: readonly ChosenOption[],
    childResults: readonly ConditionResult[],
): ConditionResult {
    const own: ConditionResultCode[] = [];
    let delegates = false;

    for (const entry of chosen) {
        const result = entry.option.result;

        if (result === null || result === undefined) {
            continue;
        }

        own.push(result);

        if (result === CONDITION_RESULT_FROM_CHILDREN) {
            delegates = true;
        }
    }

    if (own.length > 0 && !delegates) {
        return severityPick(own);
    }

    const kids: ConditionResultCode[] = [];
    let positiveSum = 0;
    let positiveCount = 0;

    for (const child of childResults) {
        if (child === null) {
            continue;
        }

        kids.push(child);

        if (child > 0) {
            positiveSum += child;
            positiveCount += 1;
        }
    }

    if (positiveCount > 0) {
        return roundToScale(positiveSum, positiveCount);
    }

    const rest = kids.filter((code) => code !== CONDITION_RESULT_NO_BUBBLE);

    if (rest.length > 0) {
        return severityPick(rest);
    }

    if (kids.length > 0) {
        return -5;
    }

    return severityPick(own);
}

/** Bottom-up over de hele boom; kinderen altijd vóór hun ouder. */
export function resolveAllResults(
    tree: RuntimeTree,
    visibility: VisibilityResult,
): Map<NodeId, ConditionResult> {
    const results = new Map<NodeId, ConditionResult>();

    for (let index = tree.order.length - 1; index >= 0; index -= 1) {
        const nodeId = tree.order[index] as NodeId;
        const node = visibility.nodes.get(nodeId) as NodeVisibility;
        const childResults = node.childIds.map((childId) => results.get(childId) ?? null);

        results.set(nodeId, resolveNodeResult(node.chosen, childResults));
    }

    return results;
}
